/**
 * AccountSetupPage — shown after login while a migrated user's authorizations are re-keyed:
 * checking (neutral loader: the in-app silent probe has not yet said whether this user is
 * migrating at all), waiting (loader with the account-setup copy), still_waiting (window expired:
 * keep waiting / sign out), blocked (backend parked the migration: sign out only). Meridian
 * primitives, mirroring PermissionsRequiredPage; sign-out runs the unified useLogout.
 *
 * Accessibility: the title/message live in a polite live region so phase changes are announced,
 * the spinner is decorative (the text carries the meaning), and focus moves to the heading when
 * actions appear (still_waiting / blocked) so keyboard and screen-reader users land on the
 * explanation next to the buttons. `appearAfterMs` lets the checking phase show its loader only
 * when the probe is actually slow, so a fast check shows nothing at all.
 */

import React, { useEffect, useRef, useState } from 'react'

import Button from '@amzn/meridian/button'
import Column from '@amzn/meridian/column'
import Loader from '@amzn/meridian/loader'
import Row from '@amzn/meridian/row'
import Text from '@amzn/meridian/text'

import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import { useLogout } from '@hooks/useLogout'

interface IAccountSetupPageProps {
    phase: 'checking' | 'waiting' | 'still_waiting' | 'blocked'
    onKeepWaiting: () => void
    /** Render nothing until this many ms have passed (used by the checking phase). */
    appearAfterMs?: number
}

const AccountSetupPage = ({ phase, onKeepWaiting, appearAfterMs = 0 }: IAccountSetupPageProps): React.ReactElement | null => {
    const [bundle] = useBundle(BUNDLE_NAME)
    const logout = useLogout()
    const [visible, setVisible] = useState(appearAfterMs === 0)
    const headingRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (visible) {
            return
        }
        const handle = window.setTimeout(() => setVisible(true), appearAfterMs)
        return () => window.clearTimeout(handle)
    }, [visible, appearAfterMs])

    // When actions appear, land focus on the heading so the state change is announced and the
    // buttons are next in tab order.
    useEffect(() => {
        if (visible && (phase === 'still_waiting' || phase === 'blocked')) {
            headingRef.current?.focus()
        }
    }, [visible, phase])

    if (!visible) {
        return null
    }

    if (phase === 'checking') {
        // Nothing is known about a migration yet, so only a neutral loader: the account-setup copy
        // would read as "your account is being moved" to every in-app user whose probe is slow.
        return (
            <Column alignmentHorizontal="center" alignmentVertical="center" spacingInset="600" spacing="400" height="100vh" data-testid="account-setup-checking">
                <span aria-hidden="true">
                    <Loader type="circular" size="medium" />
                </span>
                <div role="status" aria-live="polite">
                    <Text type="b300" color="secondary" alignment="center">{t(bundle, 'jsma-web-common-loading')}</Text>
                </div>
            </Column>
        )
    }

    if (phase === 'blocked') {
        return (
            <Column alignmentHorizontal="center" spacingInset="600" spacing="400" height="100vh" data-testid="account-setup-blocked">
                <Column spacing="400" maxWidth={420} width="100%">
                    <div role="status" aria-live="polite" ref={headingRef} tabIndex={-1}>
                        <Column spacing="300">
                            <Text type="h500">{t(bundle, 'jsma-web-accountSetup-blockedTitle')}</Text>
                            <Text type="b300" color="secondary">{t(bundle, 'jsma-web-accountSetup-blockedMessage')}</Text>
                        </Column>
                    </div>
                    <Row widths="fill">
                        <Button type="primary" size="large" onClick={logout} data-testid="account-setup-sign-out">
                            {t(bundle, 'jsma-web-accountSetup-signOut')}
                        </Button>
                    </Row>
                </Column>
            </Column>
        )
    }

    if (phase === 'still_waiting') {
        return (
            <Column alignmentHorizontal="center" spacingInset="600" spacing="400" height="100vh" data-testid="account-setup-still-waiting">
                <Column spacing="400" maxWidth={420} width="100%">
                    <div role="status" aria-live="polite" ref={headingRef} tabIndex={-1}>
                        <Column spacing="300">
                            <Text type="h500">{t(bundle, 'jsma-web-accountSetup-stillWaitingTitle')}</Text>
                            <Text type="b300" color="secondary">{t(bundle, 'jsma-web-accountSetup-stillWaitingMessage')}</Text>
                        </Column>
                    </div>
                    <Row widths="fill">
                        <Button type="primary" size="large" onClick={onKeepWaiting} data-testid="account-setup-keep-waiting">
                            {t(bundle, 'jsma-web-accountSetup-keepWaiting')}
                        </Button>
                    </Row>
                    <Row widths="fill">
                        <Button type="tertiary" size="large" onClick={logout} data-testid="account-setup-sign-out">
                            {t(bundle, 'jsma-web-accountSetup-signOut')}
                        </Button>
                    </Row>
                </Column>
            </Column>
        )
    }

    return (
        <Column alignmentHorizontal="center" alignmentVertical="center" spacingInset="600" spacing="400" height="100vh" data-testid="account-setup-waiting">
            <span aria-hidden="true">
                <Loader type="circular" size="medium" />
            </span>
            <div role="status" aria-live="polite">
                <Column spacing="300" maxWidth={420} width="100%" alignmentHorizontal="center">
                    <Text type="h500" alignment="center">{t(bundle, 'jsma-web-accountSetup-waitingTitle')}</Text>
                    <Text type="b300" color="secondary" alignment="center">{t(bundle, 'jsma-web-accountSetup-waitingMessage')}</Text>
                </Column>
            </div>
        </Column>
    )
}

export default AccountSetupPage
